import { useMemo } from 'react'

import { BidType, MarketProgram } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import type { EnrichedPtuData } from '@/features/bidding/hooks/useBidOfferEnrichedPtus'
import { isValueStackingSupported as isValueStackingSupportedUtil } from '@/features/bidding/utils/isValueStackingSupported'

import type { UseBidOfferCapacityBidsResult } from './useBidOfferCapacityBids'

export type ColumnVisibilityFlags = {
  isValueStackingSupported: boolean
  shouldDisplayCapacityBidReference: boolean
  shouldShowAcceptedCapacityVolumeColumn: boolean
}

export type UseBidOfferColumnVisibilityResult = {
  getColumnVisibility: (activationGroupUuid: string) => ColumnVisibilityFlags
}

function shouldShowCapacityBidReference(bidType: BidType, marketProgram: MarketProgram): boolean {
  const marketsThatDisplayCapacityBidReference = [MarketProgram.MFRR_UP, MarketProgram.MFRR_DOWN]
  return bidType === BidType.ENERGY && marketsThatDisplayCapacityBidReference.includes(marketProgram)
}

/**
 * Hook to compute column visibility flags for the Offered Bid PTUs DataGrid.
 * Provides a memoized function to get visibility flags per activation group.
 *
 * @param capacityBids - The capacity bids result from useBidOfferCapacityBids
 * @param enrichedPtusByActivationGroup - Map of activation group UUID to enriched PTU data
 * @returns An object containing the getColumnVisibility function
 */
export function useBidOfferColumnVisibility(
  capacityBids: UseBidOfferCapacityBidsResult,
  enrichedPtusByActivationGroup: Map<string, EnrichedPtuData>,
): UseBidOfferColumnVisibilityResult {
  const { selectedCountry } = useBiddingContext()

  const getColumnVisibility = useMemo(
    () =>
      (activationGroupUuid: string): ColumnVisibilityFlags => {
        const enrichedData = enrichedPtusByActivationGroup.get(activationGroupUuid)
        if (!enrichedData) {
          return {
            isValueStackingSupported: false,
            shouldDisplayCapacityBidReference: false,
            shouldShowAcceptedCapacityVolumeColumn: false,
          }
        }

        const { bidMetaData } = enrichedData
        const { bidType, marketProgram } = bidMetaData

        const isValueStackingSupported = isValueStackingSupportedUtil(marketProgram, selectedCountry)

        const capacityBidOnThisDay = capacityBids.hasCapacityBidOnThisDay(activationGroupUuid)
        const shouldDisplayCapacityBidReference = Boolean(
          shouldShowCapacityBidReference(bidType, marketProgram) && capacityBidOnThisDay,
        )

        const shouldShowAcceptedCapacityVolumeColumn =
          shouldDisplayCapacityBidReference &&
          capacityBids.hasCapacityBidWithAcceptedStatusOnThisDay(activationGroupUuid)

        return {
          isValueStackingSupported,
          shouldDisplayCapacityBidReference,
          shouldShowAcceptedCapacityVolumeColumn,
        }
      },
    [capacityBids, enrichedPtusByActivationGroup, selectedCountry],
  )

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  return useMemo(() => ({ getColumnVisibility }), [getColumnVisibility])
}
